import React from 'react'
import {Box, Stack, Typography} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import Card from 'src/components-new/layout/Card'
import ReadOnlyField from 'src/components-new/inputs/ReadOnlyField'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {useAppSelector} from 'src/hooks'
import {DigitalEnrollmentRow, UnitValue} from './digitalEnrollmentColumns'
import {CustomFieldDef, formatCustomFieldValue} from './customFields'
import {getDigitalEnrollmentStateColor, getDigitalEnrollmentStateLabel} from './enrollmentHubOptions'

interface Option {
  value: string
  label: string
}

interface DigitalEnrollmentDetailDialogNewProps {
  row: DigitalEnrollmentRow
  open: boolean
  onClose: () => void
  therapyTypeOptions: Option[]
  serviceTypeOptions: Option[]
}

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
  gap: 1,
} as const

function formatUnitValue(unitValue?: UnitValue): string | undefined {
  if (unitValue?.value === undefined || unitValue?.value === null) return undefined
  return unitValue.unitType ? `${unitValue.value} ${unitValue.unitType}` : String(unitValue.value)
}

/**
 * Read-only equivalent of the legacy row-click flow — in the legacy page, clicking a row
 * opens the same creation dialog with every field disabled
 * (src/pages/digitalEnrollment/DigitalEnrollment.tsx `onRowClick`). Here it's a dedicated
 * ReadOnlyField-based dialog instead, showing only the sections/fields that have data.
 */
export default function DigitalEnrollmentDetailDialogNew({
  row,
  open,
  onClose,
  therapyTypeOptions,
  serviceTypeOptions,
}: DigitalEnrollmentDetailDialogNewProps): JSX.Element {
  const {customFields = []} = useAppSelector(
    state => state.pspConfigReducer.data.enrollmentHubConfigurations?.digitalEnrollmentConfigurations ?? {}
  ) as {customFields?: CustomFieldDef[]}

  const serviceTypeLabel = serviceTypeOptions.find(opt => opt.value === row.serviceType)?.label ?? row.serviceType
  const therapyLabel = row.therapyType?.name ?? therapyTypeOptions.find(opt => opt.value === row.therapyTypeId)?.label

  const dosageRows: Array<{label: string, value?: string}> = [
    {label: 'Dosaggio', value: formatUnitValue(row.drugDosage)},
    {label: 'Dosaggio giornaliero', value: formatUnitValue(row.dailyDosage)},
    {label: 'Velocità di somministrazione', value: formatUnitValue(row.speedOfAssumption)},
    {label: 'Velocità device', value: formatUnitValue(row.speedOfDevice)},
  ].filter(item => item.value !== undefined)

  const filledCustomFields = customFields
    .map(field => ({field, entry: row.customFields?.find(cf => cf.id === field.id)}))
    .filter(({entry}) => entry !== undefined)

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Dettaglio adesione"
      maxWidth="md"
      actions={<Button variant="secondary" onClick={onClose}>{'Chiudi'}</Button>}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
        <Card title="Paziente">
          <Box sx={GRID_SX}>
            <ReadOnlyField label="Nome Paziente" value={row.firstName} fullWidth />
            <ReadOnlyField label="Cognome Paziente" value={row.lastName} fullWidth />
            <ReadOnlyField label="Email" value={row.email} fullWidth />
            <ReadOnlyField label="Numero cellulare" value={row.cellPhoneNumber} fullWidth />
            <Stack spacing={0.25} sx={{py: '10px'}}>
              <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                {'Stato'}
              </Typography>
              <Box>
                <StatusPill label={getDigitalEnrollmentStateLabel(row.state)} color={getDigitalEnrollmentStateColor(row.state)} />
              </Box>
            </Stack>
          </Box>
        </Card>

        <Card title="Terapia e dosaggi">
          <Box sx={GRID_SX}>
            {row.serviceType && <ReadOnlyField label="Tipologia servizio" value={serviceTypeLabel} fullWidth />}
            <ReadOnlyField label="Terapia" value={therapyLabel} fullWidth />
            {dosageRows.map(item => (
              <ReadOnlyField key={item.label} label={item.label} value={item.value} fullWidth />
            ))}
          </Box>
        </Card>

        {filledCustomFields.length > 0 && (
          <Card title="Informazioni aggiuntive">
            <Box sx={GRID_SX}>
              {filledCustomFields.map(({field, entry}) => (
                <ReadOnlyField
                  key={field.id}
                  label={field.label}
                  value={formatCustomFieldValue(field, entry?.value)}
                  fullWidth
                />
              ))}
            </Box>
          </Card>
        )}
      </Box>
    </AppDialog>
  )
}
